import {Injectable} from "@angular/core";
import {GridLayoutService} from "@irb-common/interfaces/grid-layout.service";
import {TradeDetailsComponent} from "@irb/irb-editors/edit-trading-instructions/trade-details/trade-details.component";
import {TruncateTooltipCellComponent} from "@irb-common/components/grid/renderers/truncatetooltip.cell.component";
import {InventoryColInfoService} from "@irb/irb-orders/order-blotter/inventory/inventory-col-info.service";
import {TradeDetailsSymbolRendererComponent} from "@irb/irb-editors/edit-trading-instructions/trade-details/trade-details-symbol.renderer.component";
import {IrbDefaultHeaderComponent} from "@irb-common/components/irebal-ag-grid-ent-avatar/irb-default-header.component";
import {PortColFormatterComponent} from '@irb-common/components/irebal-ag-grid-ent-avatar/portcolformatter.component';
import {Observable} from "rxjs";

@Injectable({
    providedIn: "root",
})
export class TradeInstructionsDetailsGridLayoutService implements GridLayoutService<TradeDetailsComponent> {

    securityTypeList: string[] = [];
    parentComp: TradeDetailsComponent;
    transTypeList = ['Buy', 'Sell', 'Swap/Exchange', 'Sell to open', 'Sell to close', 'Buy to open', 'Buy to close', 'Spread'];

    constructor(private inventoryColInfoService: InventoryColInfoService) {
    }

    getFrameworkComponents() {
        return {
            "TruncateTooltipCellComponent": TruncateTooltipCellComponent,
            "TradeDetailsSymbolRendererComponent": TradeDetailsSymbolRendererComponent,
            "cellRendererFormatter": PortColFormatterComponent
        };
    }

    getDetailFrameworkComponents() {
        return {
            "TruncateTooltipCellComponent": TruncateTooltipCellComponent,
            "agColumnHeader": IrbDefaultHeaderComponent,
            "cellRendererFormatter": PortColFormatterComponent
        };
    }

    getColumnDefs(parent: TradeDetailsComponent) {
        this.parentComp = parent;
        return [

            {
                headerName: 'Account',
                suppressMovable: true,
                lockPosition: true,
                lockVisible: true,
                suppressFiltersToolPanel: true,
                suppressColumnsToolPanel: true,
                field: 'accountNumber',
                colId: 'accountNumber',
                showRowGroup: true,
                cellRenderer: 'agGroupCellRenderer',
                minWidth: 95,
                maxWidth: 150,
                flex: 1
            },
            {
                headerName: 'Account Name',
                suppressMovable: true,
                lockPosition: true,
                lockVisible: true,
                suppressFiltersToolPanel: true,
                suppressColumnsToolPanel: true,
                field: 'accountName',
                colId: 'accountName',
                cellRendererSelector: this.cellRendererSelectorAccountName.bind(this),
                minWidth: 80,
                flex: 1
            },
            {
                headerName: 'Symbol',
                suppressMovable: true,
                lockPosition: true,
                lockVisible: true,
                suppressFiltersToolPanel: true,
                suppressColumnsToolPanel: true,
                field: 'securitySymbol',
                colId: 'securitySymbol',
                cellRenderer: TradeDetailsSymbolRendererComponent,
                cellClass: ['symbol-no-pointer'],
                minWidth: 80,
                maxWidth: 150,
                flex: 1
            },
            {
                headerName: 'Trans Type',
                suppressFiltersToolPanel: true,
                suppressColumnsToolPanel: true,
                field: 'transactionType',
                colId: 'transactionType',
                cellRendererSelector: this.cellRendererSelectorTransactionType.bind(this),
                minWidth: 75,
                maxWidth: 150,
                flex: 1
            }
        ];

    }

    getDetailColumnDefs() {
        return [
            {
                headerName: 'Order Type',
                field: 'orderType',
                colId: 'orderType',
                minWidth: 95,
                suppressMovable: true,
                lockPosition: true,
                lockVisible: true,
                suppressFiltersToolPanel: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                cellRenderer: 'cellRendererFormatter',
            },
            {
                headerName: 'Share Qty',
                field: 'quantity',
                colId: 'quantity',
                minWidth: 75,
                suppressMovable: true,
                lockPosition: true,
                lockVisible: true,
                suppressFiltersToolPanel: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                cellClass: 'irb-cell-content-right',
                cellRenderer: 'cellRendererFormatter',
                cellRendererParams: { type: "currency", displaySymbol: " ", decimalPosition: '4'}
            },
            {
                headerName: 'Price',
                field: 'price',
                colId: 'price',
                minWidth: 60,
                suppressMovable: true,
                lockPosition: true,
                lockVisible: true,
                suppressFiltersToolPanel: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                cellClass: 'irb-cell-content-right',
                cellRenderer: 'cellRendererFormatter',
            },
            {
                headerName: 'Expiration Type',
                field: 'expirationType',
                colId: 'expirationType',
                minWidth: 80,
                suppressMovable: true,
                lockPosition: true,
                lockVisible: true,
                suppressFiltersToolPanel: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                cellRenderer: 'cellRendererFormatter',
            },
            {
                headerName: 'Expiration Date',
                field: 'expirationDate',
                colId: 'expirationDate',
                minWidth: 90,
                suppressMovable: true,
                lockPosition: true,
                lockVisible: true,
                suppressFiltersToolPanel: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                cellRenderer: (params: any) => {
                    return params.value ? (new Date(params.value)).toLocaleDateString() : '';
                }
            },
            {
                headerName: 'Quote',
                field: 'currentUnitPrice',
                colId: 'currentUnitPrice',
                minWidth: 60,
                suppressMovable: true,
                lockPosition: true,
                lockVisible: true,
                suppressFiltersToolPanel: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                cellClass: 'irb-cell-content-right',
                cellRenderer: 'cellRendererFormatter',
            },
            {
                headerName: '10-Day Avg Vol',
                field: 'avgDailyTradeVol',
                colId: 'avgDailyTradeVol',
                minWidth: 110,
                suppressMovable: true,
                lockPosition: true,
                lockVisible: true,
                suppressFiltersToolPanel: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                cellClass: 'irb-cell-content-right',
                cellRenderer: 'cellRendererFormatter',
            },
            {
                headerName: '% of Avg Vol',
                field: 'perShrAmt',
                colId: 'perShrAmt',
                minWidth: 65,
                suppressMovable: true,
                lockPosition: true,
                lockVisible: true,
                suppressFiltersToolPanel: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                cellClass: 'irb-cell-content-right',
                cellRenderer: 'cellRendererFormatter',
            }
        ];
    }

    getColumnDefsBlockOrder() {
        return [
            {
                headerName: '',
                suppressFiltersToolPanel: true,
                children: [
                        {
                            headerName: 'Account',
                            suppressFiltersToolPanel: true,
                            field: 'accountNumber',
                            colId: 'accountNumber',
                            cellRenderer: 'cellRendererFormatter',
                            cellClass: 'irb-cell-content-right',
                            minWidth: 95,
                            filter: 'agTextColumnFilter',
                            suppressFloatingFilterButton: true,
                            filterParams: {
                                // applyButton: true,
                                clearButton: true,
                                debounceMs: 1000,
                                // filterOptions: [],
                                textFormatter: this.textFilterFormatter,
                                maxNumConditions: 1,
                                // values: (params: any) => {
                                //    params.success(this.modelNames);
                                // }
                            }
                        }
                    ]
            },
            {
                headerName: '',
                suppressFiltersToolPanel: true,
                children: [
                    {
                        headerName: 'Account Name',
                        suppressFiltersToolPanel: true,
                        field: 'accountName',
                        colId: 'accountName',
                        cellRenderer: 'cellRendererFormatter',
                        minWidth: 80,
                        filter: 'agTextColumnFilter',
                        suppressFloatingFilterButton: true,
                        filterParams: {
                            clearButton: true,
                            debounceMs: 1000,
                            textFormatter: this.textFilterFormatter,
                            maxNumConditions: 1,
                        }
                    }
                ]                
            },
            {
                headerName: 'Trade Details',
                suppressFiltersToolPanel: true,
                children: [
                    {
                        headerName: 'Trans Type',
                        suppressFiltersToolPanel: true,
                        field: 'transactionType',
                        colId: 'transactionType',
                        cellRenderer: 'cellRendererFormatter',
                        minWidth: 75,
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            values: (params: any) => {
                                params.success(this.transTypeList);
                            },
                            valueFormatter: (params: any) => {
                                return this.formatTransaction(params.value);
                            },
                            buttons:['reset', 'apply'],
                            newRowsAction: 'keep',
                            suppressSorting: true,
                            cellHeight: 30,
                            closeOnApply: true                           
                        },
                    },
                    {
                        headerName: 'Shr Qty',
                        headerComponentParams: {
                            irbColInfo: this.inventoryColInfoService.shrQtyColInfo()
                        },
                        suppressFiltersToolPanel: true,
                        field: 'quantity',
                        colId: 'quantity',
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: { type: "currency", displaySymbol: " ", decimalPosition: '4'},
                        cellClass: 'irb-cell-content-right',
                        minWidth: 75,
                    },
                    {
                        headerName: 'Order Amt',
                        suppressFiltersToolPanel: true,
                        field: 'dollarAmount',
                        colId: 'dollarAmount',
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: {type: 'currency'},
                        minWidth: 75,
                    }
                ]
            },
            {
                headerName: 'Trade Authorization',
                suppressFiltersToolPanel: true,
                children: [
                    {
                        headerName: 'Sol',
                        suppressFiltersToolPanel: true,
                        field: 'isSolicited',
                        colId: 'isSolicited',
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: {type: 'checkbox', isCheckboxDisabled: true},
                        minWidth: 50,
                    },
                    {
                        headerName: 'Disc',
                        suppressFiltersToolPanel: true,
                        field: 'isDiscretionary',
                        colId: 'isDiscretionary',
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: {type: 'checkbox', isCheckboxDisabled: true},
                        minWidth: 50,
                    }
                ]
            },
            {
                headerName: 'History',
                suppressFiltersToolPanel: true,
                children: [
                    {
                        headerName: 'Order History',
                        suppressFiltersToolPanel: true,
                        field: 'orderHistory',
                        colId: 'orderHistory',
                        cellRenderer: 'cellRendererFormatter',
                        minWidth: 60,
                    },
                    {
                        headerName: 'Approval Date',
                        suppressFiltersToolPanel: true,
                        field: 'createdDate',
                        colId: 'createdDate',
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: {
                            type: 'date', format: 'MM/dd/yyyy h:mm a', displayETtext: true
                        },
                        minWidth: 100,
                    },
                    {
                        headerName: 'Rebal ID',
                        suppressFiltersToolPanel: true,
                        field: 'rebalanceResultId',
                        colId: 'rebalanceResultId',

                        cellClass: 'irb-cell-content-right',
                        minWidth: 75,
                    }
                ]
            }


        ];
    }

    private textFilterFormatter(r: any) {
        if (r == null) {
            return null;
        }

        return r;
    }

    formatTransaction(item: string) {
        switch (item) {
            case 'BUY':
                return 'Buy';
            case 'SELL':
                return 'Sell';
            case 'SELL_ALL':
                return 'Sell All';
            case 'SWAPEXCHANGE':
                return 'Swap/Exchange';
            case 'SWAP':
                return 'Swap';
            case 'SWAP_ALL':
                return 'Swap All';
            case 'EXCHANGE':
                return 'Exch';
            case 'EXCHANGE_ALL':
                return 'Exch All';
            case 'SELL_TO_OPEN':
                return 'Sell to open';
            case 'SELL_TO_CLOSE':
                return 'Sell to close';
            case 'BUY_TO_OPEN':
                return 'Buy to open';
            case 'BUY_TO_CLOSE':
                return 'Buy to close';
            case 'SPREAD':
                return 'Spread';
            default:
                return item;
        }
    }

    cellRendererSelectorAccountName(params: any) {
        if (params.data) {
            if (params.data.isBlockOrder) {
                return {
                    component: 'cellRendererFormatter',
                    params: {value: "Combined"},
                };
            } else {
                return {
                    component: 'cellRendererFormatter',
                    params: {value: params.data.accountName},
                };
            }
        }
    }

    cellRendererSelectorTransactionType(params: any) {
        if (params.data) {
            return {
                component: 'cellRendererFormatter',
                params: {value: this.formatTransaction(params.data.transactionType)}
            };
        }
    }

    cellRendererSelectorBlockOrders(tradeDetailsComponent: TradeDetailsComponent, params: any,) {

        if (params && params.data && params.data.isBlockOrder) {
            return {
                component: "cellRendererFormatter",
                params: {
                    link: (params: any) => {
                        return params.value !== null && params.value.length > 0;
                    },
                    onClick: function () {
                        this.pfs.getComponentOrders(params.data.orderID, params.context.componentParent.current, params.data.createdDate, this.pfs.isArchivedOrders).subscribe((res: any) => {
                            if (res != null) {
                                tradeDetailsComponent.blockOrdersDialog(res, params.data);
                            }
                        });
                    }.bind(this),
                },
            };
        }

    }
}
